/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useRef, useState, useEffect } from 'react';
import SkillStats from './model/SkillStats';
import AttributeValues from "./model/AttributeValues";
import SkillSet, { Skill } from './model/SkillSet';

interface SkillsSelectorProps {
  skillSet: SkillSet;
  attributes: AttributeValues;
  proficiency: number;
  alwaysShow: boolean;
  useMm2024: boolean;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSave: (newSkillSet: SkillSet) => void;
}

const SkillsSelector: React.FC<SkillsSelectorProps> = ({skillSet, attributes, proficiency, alwaysShow, useMm2024, isOpen, setIsOpen, onSave}) => {
  const panelRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [customString, setCustomString] = useState<string>('');

  const [skillStatsByName, setSkillStatsByName] = useState<Map<string, keyof AttributeValues | 'unknown'>>(SkillStats.getSkillsMap());

  useEffect(() => {
    // Calling updateLevels modifying the Skill elements in the skills.skills Record, by using these attribute & proficiency values:
    const newSkillSet = SkillSet.updateLevels(skillSet, attributes, proficiency);
    setCustomString(newSkillSet.custom || '');
    for (const skillName of skillStatsByName.keys()) {
      if (!SkillStats.getSkillsMap().has(skillName)) {
        if (!newSkillSet.skills[skillName]) {
          skillStatsByName.delete(skillName);
        }
      }
    }
    for (const skillName in skillSet.skills) {
      if (!skillStatsByName.has(skillName)) {
        skillStatsByName.set(skillName, 'unknown');
      }
    }
  }, [skillSet, attributes, proficiency])

  const handleSkillClick = (event: React.MouseEvent<HTMLLIElement>, skillName: string) => {
    event.stopPropagation(); // Stop event propagation to prevent closing the panel
    let newSkillSet = structuredClone(skillSet);
    let skill = newSkillSet.skills[skillName];
    if (!skill) {
      const attribute = SkillStats.getSkillsMap().get(skillName);
      if (!attribute) {
        return;
      }
      skill = new Skill();
      skill.attribute = attribute;
      skill.level = 'proficient';
      newSkillSet.skills[skillName] = skill;
    } else if (skill.attribute === 'unknown') {
        return; // ignore clicks on custom skills
    } else if (skill.level === 'expert') {
      // delete the skill named skillName from the Records:
      newSkillSet.skills = (({ [skillName]: _, ...rest }) => rest)(newSkillSet.skills);
    } else if (skill.level === 'proficient') {
      skill.level = 'expert';
    } else if (skill.level === 'unskilled') {
      skill.level = 'proficient';
    }
    skill.bonus = 0;
    newSkillSet = SkillSet.updateLevels(newSkillSet, attributes, proficiency);

    onSave(newSkillSet);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      panelRef.current && !panelRef.current.contains(event.target as Node) &&
      textRef.current && !textRef.current.contains(event.target as Node) &&
      titleRef.current && !titleRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Cleanup code when the component is unmounted
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if content is empty
  if (Object.keys(skillSet.skills).length === 0 && !alwaysShow && !isOpen) {
    return null;
  }

  const handleCustomChange = (newCustomValue: string) => {
    const newSkillSet = SkillSet.parseFromString(newCustomValue, attributes, proficiency);
    setCustomString(newSkillSet.custom || '');
    onSave(newSkillSet);
  }

  const getSkillDisplay = (skillName: string, attr: keyof AttributeValues | 'unknown') => {
    const skill = skillSet.skills[skillName];
    let val = '---';
    if (skill) {
      if (skill.bonus != 0) {
        val = '' + ((skill.adjLevel > 0) ? '+' : '') + skill.adjLevel;
      } else if (skill.level === 'proficient') {
        val = 'Prof';
      } else if (skill.level === 'expert') {
        val = 'Exp';
      } else if (skill.bonus != 0) {
        val = '' + ((skill.adjLevel > 0) ? '+' : '') + skill.adjLevel;
      }
    }

    return (<li key={skillName} onClick={(event) => handleSkillClick(event, skillName)}>
              <span className='skillLevel'>
                {val}
              </span>
              <span>&nbsp;{skillName}&nbsp;<span className='skill-attribute'>({attr === 'unknown' ? 'custom' : attr.substring(0, 3)})</span></span>
            </li>);

  }
  let displayStr = customString.replace(/ /g, '\u00A0').replace(/,\u00A0/g, ', ').replace(/[ ,]*$/, '') || '--';
  if (useMm2024) {
    // Is 'Initiative +4' the only skill we are displaying, but it's not going to be shown because it's in the MM2024 location near AC?
    if (displayStr.match(/^initiative\u00A0\+\d* *$/i) && !alwaysShow && !isOpen) {
      return null;
    }
    displayStr = displayStr.replace(/^initiative\u00A0\+\d* *$/gi, alwaysShow ? ' -- ' : '')
                           .replace(/^initiative\u00A0\+\d* *,?/gi, '')
                           .replace(/, initiative\u00A0\+\d*/gi, '');
  }
  return (
    <div className="property-line">
      <h4 ref={titleRef}>Skills</h4>
      <span className="multi-select-container edit-field">
        <span className="property-line" onMouseDown={() => setIsOpen(true)} ref={textRef}>
          <p>{displayStr}</p>
        </span>
        {isOpen && (
          <span className="multi-select-panel" ref={panelRef}>
            <table>
              <tbody>
                <tr>
                  <td>
                    <ul>
                      {[...skillStatsByName.entries()].map(([skill, attr], index, arry) => (
                        (index < (arry.length / 2)) && getSkillDisplay(skill, attr)
                      ))}
                    </ul>
                  </td>
                  <td>
                    <ul>
                      {[...skillStatsByName.entries()].map(([skill, attr], index, arry) => (
                        (index >= (arry.length / 2)) && getSkillDisplay(skill, attr)
                      ))}
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td colSpan={2} style={{padding: '10px', paddingTop: '0px'}}>Custom:
                    <input style={{width: '97%'}} type="text" value={skillSet.custom || ''} onChange={e => handleCustomChange(e.target.value)}></input>
                  </td>
                </tr>
              </tbody>
            </table>
          </span>
        )}
      </span>
    </div>
  );
};

export default SkillsSelector;
